import { useEffect, useRef } from "react";
import { HeroMedia } from "@/components/HeroMedia";
import { HeroScrollCta } from "@/components/HeroScrollCta";
import { useHeroVideoUrls } from "@/hooks/useHeroVideoUrls";
import { useCollectionOverlays } from "@/hooks/useCollectionOverlays";
import { useIsMobile } from "@/hooks/use-mobile";
import { useLocale } from "@/contexts/LocaleContext";
import { ImageHotspots } from "@/components/ImageHotspots";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { t } from "@/lib/i18n";

const HERO_HANDLE = "__hero__";

type HeadingTag = "h1" | "div";

type HeroTextPosition =
  | "bottom-left"
  | "bottom-center"
  | "bottom-right"
  | "center"
  | "center-left"
  | "center-right"
  | "top-left"
  | "top-center"
  | "top-right";

const positionMap: Record<
  HeroTextPosition,
  { container: string; item: string; text: string; pad: string }
> = {
  "bottom-left": {
    container: "justify-end items-start",
    item: "items-start text-start",
    text: "text-start",
    pad: "pb-[clamp(36px,7vw,92px)]",
  },
  "bottom-center": {
    container: "justify-end items-center",
    item: "items-center text-center",
    text: "text-center",
    pad: "pb-[clamp(36px,7vw,92px)]",
  },
  "bottom-right": {
    container: "justify-end items-end",
    item: "items-end text-end",
    text: "text-end",
    pad: "pb-[clamp(36px,7vw,92px)]",
  },
  center: {
    container: "justify-center items-center",
    item: "items-center text-center",
    text: "text-center",
    pad: "py-0",
  },
  "center-left": {
    container: "justify-center items-start",
    item: "items-start text-start",
    text: "text-start",
    pad: "py-0",
  },
  "center-right": {
    container: "justify-center items-end",
    item: "items-end text-end",
    text: "text-end",
    pad: "py-0",
  },
  "top-left": {
    container: "justify-start items-start",
    item: "items-start text-start",
    text: "text-start",
    pad: "pt-[clamp(36px,7vw,92px)]",
  },
  "top-center": {
    container: "justify-start items-center",
    item: "items-center text-center",
    text: "text-center",
    pad: "pt-[clamp(36px,7vw,92px)]",
  },
  "top-right": {
    container: "justify-start items-end",
    item: "items-end text-end",
    text: "text-end",
    pad: "pt-[clamp(36px,7vw,92px)]",
  },
};

/**
 * HomeHero — يعرض محتوى الهيرو فقط (وسائط + نصّ + CTA).
 * الخلفيّة العامّة للصفحة مسؤوليّة <PageBackground /> في SiteShell
 * (z-0 fixed، يظهر خلف باقي الصفحة بعد الهيرو). ممنوع خلط النظامين.
 */
export function HomeHero({
  className,
  /**
   * وسم العنوان. الصفحة الرئيسيّة تُركِّب HomeHero مرّتين (فرع الجوّال وفرع
   * الديسكتوب) وكلاهما في الـDOM، فيجب أن يحمل واحدٌ فقط <h1> — القاعدة:
   * عنوان رئيسيّ واحد لكلّ صفحة. الفرع الثاني يُمرَّر له "div".
   */
  headingTag = "h1",
  page = "home",
  initialHero,
}: {
  className?: string;
  headingTag?: HeadingTag;
  page?: string;
  /** ⚡ LCP: صورة الهيرو من loader الصفحة لترسم في أوّل HTML. */
  initialHero?: { mobileImage?: string | null; desktopImage?: string | null } | null;
}) {
  const Heading = headingTag;
  const heroRef = useRef<HTMLDivElement>(null);
  const { desktop, mobile, poster, campaignId, text: campaignText, heightMode } = useHeroVideoUrls(page);
  const isMobile = useIsMobile();
  const { locale } = useLocale();
  const heroHandle = page === "home" ? HERO_HANDLE : `__hero_${page}__`;
  const { data: overlays } = useCollectionOverlays([heroHandle]);
  const overlay = overlays?.[heroHandle];

  useEffect(() => {
    // Parallax مُعطَّل على الموبايل: يسبّب اهتزاز الفيديو مع شريط URL في iOS
    const mq = window.matchMedia("(min-width: 768px)");
    if (!mq.matches) return;

    const onScroll = () => {
      if (!heroRef.current) return;
      const y = window.scrollY;
      heroRef.current.style.transform = `translate3d(0, ${y * 0.25}px, 0)`;
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isAr = locale === "ar";

  // أولويّة النصّ: حملة نشطة (لو عبّى الأدمن حقلاً) → overlay `__hero__` → "lecmo".
  const pick = (ar: string | null | undefined, en: string | null | undefined) =>
    ((isAr ? ar : en) || en || ar || "").trim() || null;
  const campaignTitle = pick(campaignText?.title_ar, campaignText?.title_en);
  const campaignSubtitle = pick(campaignText?.subtitle_ar, campaignText?.subtitle_en);
  const campaignCtaLabel = pick(campaignText?.cta_label_ar, campaignText?.cta_label_en);
  const campaignCtaHref = (campaignText?.cta_href ?? "").trim() || null;

  // حملة نشطة = مصدر الحقيقة الوحيد للنصّ: لو تركت حقولها فارغة → لا نصّ إطلاقاً.
  const hasCampaignText = Boolean(campaignTitle || campaignSubtitle || campaignCtaLabel);
  const hasActiveCampaign = Boolean(campaignId);

  const title = hasActiveCampaign
    ? campaignTitle
    : (isAr ? overlay?.title_ar : overlay?.title_en) ??
      overlay?.title_en ??
      overlay?.title_ar ??
      "lecmo";
  const subtitle = hasActiveCampaign
    ? campaignSubtitle
    : (isAr ? overlay?.subtitle_ar : overlay?.subtitle_en) ??
      overlay?.subtitle_en ??
      overlay?.subtitle_ar ??
      null;
  const ctaLabel = hasActiveCampaign
    ? campaignCtaLabel
    : (isAr ? overlay?.cta_label_ar : overlay?.cta_label_en) ??
      overlay?.cta_label_en ??
      overlay?.cta_label_ar ??
      null;
  const ctaHref = hasActiveCampaign ? campaignCtaHref : overlay?.cta_href ?? null;
  const hasOverlay = Boolean(overlay);
  const showOverlayCard = hasActiveCampaign ? hasCampaignText : hasOverlay;
  const isBrand = !hasActiveCampaign && !showOverlayCard && title === "lecmo";

  // Nemro/Dawn hero — huge serif, thin underline link. الموضع يأتي من الحملة.
  const textPosition = (campaignText?.text_position ?? "bottom-left") as HeroTextPosition;
  const showHeroContent = showOverlayCard || isBrand;


  const heightClass = {
    full: "h-[100dvh] min-h-[100dvh] md:h-[calc(100svh-96px)] md:min-h-[620px]",
    large: "h-[100dvh] min-h-[100dvh] md:h-[75svh] md:min-h-[480px]",
    medium: "h-[60dvh] min-h-[60dvh] md:h-[50svh] md:min-h-[340px]",
    compact: "h-[45dvh] min-h-[45dvh] md:h-[35svh] md:min-h-[260px]",
  }[heightMode] ?? "h-[100dvh] min-h-[100dvh] md:h-[calc(100svh-96px)] md:min-h-[620px]";

  return (
    <section className={cn("relative overflow-hidden", heightClass, className)}>
      <div ref={heroRef} className="absolute inset-0 will-change-transform">
        <HeroMedia
          videoUrl={desktop}
          mobileVideoUrl={mobile}
          poster={poster || ""}
          alt="Lecmo — house of modern perfumery"
          className="h-full w-full object-cover"
          initialImageDesktop={initialHero?.desktopImage ?? null}
          initialImageMobile={initialHero?.mobileImage ?? null}
          showToggle
        />
        {/* Nemro veil — light top, dark bottom for text legibility */}
        
      </div>

      {showHeroContent && (
        <HeroTextLayer
          position={textPosition}
          isBrand={isBrand}
          subtitle={subtitle}
          title={title ?? ""}
          ctaLabel={ctaLabel}
          ctaHref={ctaHref}
          Heading={Heading}
        />
      )}

      <ImageHotspots scope="home_hero" scopeId={`${page === "home" ? "default" : page}:${isMobile ? "mobile" : "desktop"}`} />
      {campaignId && (
        <ImageHotspots scope="hero_campaign" scopeId={`${campaignId}:${isMobile ? "mobile" : "desktop"}`} />
      )}

      <HeroScrollCta pageKey={page} campaignOverride={campaignText} />


    </section>
  );
}

function HeroTextLayer({
  position,
  isBrand,
  subtitle,
  title,
  ctaLabel,
  ctaHref,
  Heading,
}: {
  position: HeroTextPosition;
  isBrand: boolean;
  subtitle: string | null;
  title: string;
  ctaLabel: string | null;
  ctaHref: string | null;
  Heading: "h1" | "div";
}) {
  const { locale } = useLocale();
  const cfg = positionMap[position];

  return (
    // dir="ltr" مقصود: موضع النصّ فوق الهيرو فيزيائيّ (يسار يبقى يسار في العربيّة أيضاً)
    // واتّجاه كلّ نصّ على حدة يُحسم بـ dir="auto".
    <div
      dir="ltr"
      className={cn(
        "pointer-events-none absolute inset-0 z-10 flex flex-col",
        cfg.container,
      )}
    >
      <div
        className={cn(
          "pointer-events-auto flex max-w-[min(760px,86vw)] flex-col px-[clamp(22px,5vw,78px)] text-white",
          cfg.item,
          cfg.pad,
        )}
      >
        {(subtitle || isBrand) && (
          <p
            dir="auto"
            className="mb-3 text-[10px] font-medium uppercase tracking-[0.22em] md:mb-4"
          >
            {subtitle ?? t(locale, "hero.eyebrow")}
          </p>
        )}
        <Heading
          dir="auto"
          className={cn(
            "m-0 font-serif font-normal text-white",
            "text-[clamp(44px,7vw,92px)] leading-[0.98] tracking-[-0.035em]",
            isBrand && "lowercase",
            cfg.text,
          )}
          style={{ textWrap: "balance" }}
        >
          {isBrand ? (
            <>
              <span dir="ltr">lecmo</span>
              <span
                dir="auto"
                className="mt-3 block text-[11px] font-sans font-medium uppercase tracking-[0.28em] opacity-80 md:text-[12px]"
              >
                {locale === "ar" ? "بيت العطر الحديث" : "House of Modern Perfumery"}
              </span>
            </>
          ) : (
            title
          )}
        </Heading>
        {ctaLabel && ctaHref ? (
          <a
            href={ctaHref}
            dir="auto"
            className="mt-6 inline-flex min-h-[40px] items-center gap-2 border-b border-white pb-1.5 text-[10px] uppercase tracking-[0.14em] text-white transition-opacity hover:opacity-80 md:mt-8"
          >
            {ctaLabel} <ArrowRight className="h-3 w-3" />
          </a>
        ) : null}
      </div>
    </div>
  );
}
